'use client';

import { useCallback, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { parseFiltersFromQuery } from '@/utils/algolia/algoliaHelper';

function useSearchUrlParams(searchIndexName) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const urlParams = useMemo(() => {
    const paramsObj = {};
    for (const [key, value] of searchParams.entries()) {
      paramsObj[key] = value;
    }
    return paramsObj;
  }, [searchParams]);

  const updateUrlParams = useCallback(
    (newParams) => {
      const current = new URLSearchParams(searchParams.toString());

      Object.entries(newParams).forEach(([key, val]) => {
        if (val === undefined || val === '') {
          current.delete(key);
        } else {
          current.set(key, val);
        }
      });

      const queryString = current.toString();
      const newUrl = `${pathname}${queryString ? `?${queryString}` : ''}`;
      window.history.pushState({}, '', newUrl);
    },
    [searchParams, pathname]
  );

  const selectedFilterKeys = useMemo(
    () => parseFiltersFromQuery(urlParams),
    [urlParams.filters]
  );

  const sortIndex = useMemo(
    () => urlParams.sort || searchIndexName,
    [urlParams.sort, searchIndexName]
  );

  const setFilters = useCallback(
    (newFilters, options = {}) => {
      updateUrlParams({ filters: newFilters.join(',') }, options);
    },
    [updateUrlParams]
  );

  const setSort = useCallback(
    (sortValue, options = {}) => {
      updateUrlParams({ sort: sortValue }, options);
    },
    [updateUrlParams]
  );

  const resetFilters = useCallback(
    (options = {}) => {
      updateUrlParams({ filters: undefined }, options);
    },
    [updateUrlParams]
  );

  return {
    selectedFilterKeys,
    sortIndex,
    setFilters,
    setSort,
    resetFilters,
    urlParams,
  };
}

export default useSearchUrlParams;
